/**
 * MN Image text slider — swipeable image + caption carousel.
 * Seamless infinite loop via cloned slides (no jump back to start).
 */
class MnImageTextSlider extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.realCount = 0;
    this.slides = [];
    this.dots = [];
    this.track = null;
    this.viewport = null;
    this.prevButton = null;
    this.nextButton = null;
    this.autoplayTimer = null;
    this.resizeObserver = null;
    this.abortController = null;
    this.reduceMotion = null;
    this.hoverMedia = null;
    this.isPointerInside = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.dragLastX = 0;
    this.dragLastTime = 0;
    this.dragVelocity = 0;
    this.dragRaf = null;
    this.settleDurationMs = null;
    this.isDragging = false;
    this.dragMoved = false;
    this.isJumping = false;
    this.onTransitionEnd = this.onTransitionEnd.bind(this);

    this.goTo = this.goTo.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.update = this.update.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
  }

  connectedCallback() {
    this.cleanup();
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.track = this.querySelector('[data-mn-its-track]');
    this.viewport = this.querySelector('.mn-image-text-slider__viewport');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = [...this.querySelectorAll('[data-mn-its-dot]')];

    if (!(this.track instanceof HTMLElement)) return;

    this.setupSlides();
    if (this.slides.length === 0) return;

    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', this.prev, { signal });
    this.nextButton?.addEventListener('click', this.next, { signal });
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick, { signal }));
    this.track.addEventListener('transitionend', this.onTransitionEnd, { signal });
    this.addEventListener('keydown', this.handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal });

    const dragTarget = this.viewport instanceof HTMLElement ? this.viewport : this;
    dragTarget.addEventListener('pointerdown', this.onPointerDown, { signal });
    dragTarget.addEventListener('pointermove', this.onPointerMove, { signal });
    dragTarget.addEventListener('pointerup', this.onPointerUp, { signal });
    dragTarget.addEventListener('pointercancel', this.onPointerUp, { signal });
    dragTarget.addEventListener('lostpointercapture', this.onPointerUp, { signal });
    dragTarget.addEventListener('click', this.onClickCapture, { capture: true, signal });

    this.addEventListener(
      'pointerenter',
      (event) => {
        if (!this.canHoverPause || event.pointerType === 'touch') return;
        this.isPointerInside = true;
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.isPointerInside = false;
        if (!this.isDragging) this.startAutoplay();
      },
      { signal }
    );
    this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (this.contains(event.relatedTarget)) return;
        window.requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });
      },
      { signal }
    );

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.update, { signal });
    }

    if (this.realCount > 1) this.setAttribute('tabindex', '0');
    this.syncArrowVisibility();
    this.goTo(this.loopEnabled ? this.realCount : 0, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  setupSlides() {
    // Remove prior clones from hot-reload / re-connect
    this.track.querySelectorAll('[data-mn-its-clone]').forEach((node) => node.remove());

    const originals = [...this.track.querySelectorAll('[data-mn-its-slide]:not([data-mn-its-clone])')];
    this.realCount = originals.length;

    if (this.loopEnabled && this.realCount > 1) {
      // Clone enough sets so the viewport never shows a gap while wrapping
      const setsNeeded = Math.max(2, Math.ceil(3));
      for (let set = 0; set < setsNeeded; set += 1) {
        originals.forEach((slide) => {
          const clone = slide.cloneNode(true);
          clone.removeAttribute('id');
          clone.removeAttribute('data-active');
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('data-mn-its-clone', '');
          clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          this.track.appendChild(clone);
        });
      }
    }

    this.slides = [...this.track.querySelectorAll('[data-mn-its-slide]')];
  }

  get canHoverPause() {
    return this.hoverMedia?.matches === true;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.realCount > 1;
  }

  get autoplaySpeed() {
    const speed = Number.parseInt(this.dataset.autoplaySpeed || '5000', 10);
    return Number.isFinite(speed) && speed >= 1000 ? speed : 5000;
  }

  get loopEnabled() {
    return this.dataset.loop === 'true' && this.realCount > 1;
  }

  get showArrows() {
    return this.dataset.showArrows === 'true' && this.realCount > 1;
  }

  get showDots() {
    return this.dataset.showDots === 'true' && this.realCount > 1;
  }

  get transitionMs() {
    const duration = Number.parseInt(this.dataset.transition || '500', 10);
    return Number.isFinite(duration) && duration >= 0 ? duration : 500;
  }

  /** Logical slide index (0 … realCount-1) for dots / a11y */
  get logicalIndex() {
    if (this.realCount <= 0) return 0;
    return ((this.index % this.realCount) + this.realCount) % this.realCount;
  }

  syncArrowVisibility() {
    this.updateArrowState();
  }

  updateArrowState() {
    if (!(this.prevButton instanceof HTMLButtonElement) || !(this.nextButton instanceof HTMLButtonElement)) {
      return;
    }

    const arrowsEnabled = this.showArrows;
    const multi = this.realCount > 1;

    if (!arrowsEnabled || !multi) {
      this.prevButton.hidden = true;
      this.nextButton.hidden = true;
      this.prevButton.disabled = true;
      this.nextButton.disabled = true;
      return;
    }

    if (this.loopEnabled) {
      this.prevButton.hidden = false;
      this.nextButton.hidden = false;
      this.prevButton.disabled = false;
      this.nextButton.disabled = false;
      return;
    }

    const hasPrev = this.index > 0;
    const hasNext = this.index < this.realCount - 1;

    this.prevButton.hidden = !hasPrev;
    this.nextButton.hidden = !hasNext;
    this.prevButton.disabled = !hasPrev;
    this.nextButton.disabled = !hasNext;
  }

  updateDots() {
    if (!this.showDots || this.dots.length === 0) return;
    const activeIndex = this.logicalIndex;

    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === activeIndex;
      dot.classList.toggle('is-active', active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  updateSlideState() {
    const activeLogical = this.logicalIndex;
    this.slides.forEach((slide, slideIndex) => {
      const logical = slideIndex % this.realCount;
      const active = logical === activeLogical && slideIndex === this.index;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('data-active', active);
    });
  }

  slideStep() {
    const first = this.slides[0];
    if (!(first instanceof HTMLElement)) return 0;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  /** How many slides fit in the visible viewport row */
  visibleSlideCount() {
    const step = this.slideStep();
    const width = this.viewport?.clientWidth || this.clientWidth || 0;
    if (!step || !width) return 1;
    return Math.max(1, Math.round(width / step));
  }

  baseOffset() {
    return this.slideStep() * this.index;
  }

  setTrackOffset(offset, animate = true) {
    if (!(this.track instanceof HTMLElement)) return;
    const reduce = this.reduceMotion?.matches;
    if (animate && !reduce) {
      const duration = this.settleDurationMs != null ? this.settleDurationMs : this.transitionMs;
      this.track.style.transitionDuration = `${duration}ms`;
      this.track.style.transitionTimingFunction =
        this.settleDurationMs != null
          ? 'cubic-bezier(0.16, 1, 0.3, 1)'
          : 'cubic-bezier(0.22, 1, 0.36, 1)';
    } else {
      this.track.style.transitionDuration = '0ms';
    }
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  /**
   * Snap the whole visible row based on drag distance (not one slide at a time).
   * Flick velocity adds momentum so a long swipe can cross several items smoothly.
   */
  settleAfterDrag(deltaX, velocity) {
    const step = this.slideStep() || 240;
    const visible = this.visibleSlideCount();

    // Where the track would coast to (px). Drag left (neg delta) → positive offset.
    const momentumPx = velocity * 160;
    const projectedOffset = this.baseOffset() - deltaX - momentumPx;
    let targetIndex = Math.round(projectedOffset / step);

    // Tiny nudge: stay put unless clearly past a fraction of a slide
    const rawDeltaSlides = targetIndex - this.index;
    if (rawDeltaSlides === 0 && Math.abs(velocity) > 0.5) {
      targetIndex = this.index + (velocity < 0 ? 1 : -1);
    }

    // Cap a single swipe to about one visible row (+1) so loop wraps stay smooth
    const maxJump = Math.max(1, visible);
    const clampedDelta = Math.max(-maxJump, Math.min(maxJump, targetIndex - this.index));
    targetIndex = this.index + clampedDelta;

    if (!this.loopEnabled) {
      targetIndex = Math.max(0, Math.min(targetIndex, this.realCount - 1));
    } else {
      // Keep runway inside the cloned track for a seamless repeat
      if (this.index < this.realCount || this.index > this.realCount * 2) {
        this.normalizeIndex();
        targetIndex = this.index + clampedDelta;
      }
      targetIndex = Math.max(0, Math.min(targetIndex, this.slides.length - 1));
    }

    const distance = Math.abs(targetIndex - this.index) * step + Math.abs(deltaX) * 0.15;
    this.settleDurationMs = this.reduceMotion?.matches
      ? 0
      : Math.round(Math.min(760, Math.max(300, 300 + distance * 0.45)));

    try {
      this.goTo(targetIndex, true);
    } finally {
      this.settleDurationMs = null;
    }
  }

  /**
   * Keep the visual index inside the first cloned range so we can always
   * animate forward/back without hitting the track end.
   */
  normalizeIndex() {
    if (!this.loopEnabled || this.realCount <= 1) return false;

    const min = this.realCount;
    const max = this.realCount * 2 - 1;

    if (this.index >= min && this.index <= max) return false;

    // Jump by one full set so the same logical slides stay under the viewport
    const sets = Math.floor(this.index / this.realCount);
    const targetSet = 1;
    const delta = (targetSet - sets) * this.realCount;
    if (delta === 0) return false;

    this.isJumping = true;
    this.index += delta;
    this.setTrackOffset(this.baseOffset(), false);
    // Force reflow so the next animated move isn't skipped
    void this.track.offsetWidth;
    this.isJumping = false;
    return true;
  }

  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    if (this.isDragging || this.isJumping) return;
    this.normalizeIndex();
  }

  goTo(index, animate = true) {
    if (this.slides.length === 0) return;

    let nextIndex = index;

    if (!this.loopEnabled) {
      nextIndex = Math.max(0, Math.min(index, this.realCount - 1));
    } else {
      nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    }

    this.index = nextIndex;
    this.setTrackOffset(this.baseOffset(), animate);
    this.updateSlideState();
    this.updateArrowState();
    this.updateDots();
  }

  next() {
    if (this.loopEnabled) {
      if (this.index >= this.slides.length - 1) {
        this.normalizeIndex();
      }
      this.goTo(this.index + 1);
    } else if (this.index < this.realCount - 1) {
      this.goTo(this.index + 1);
    }
    this.restartAutoplay();
  }

  prev() {
    if (this.loopEnabled) {
      if (this.index <= 0) {
        this.normalizeIndex();
      }
      // If still at the very start, jump into the middle set first
      if (this.index === 0) {
        this.isJumping = true;
        this.index = this.realCount;
        this.setTrackOffset(this.baseOffset(), false);
        void this.track.offsetWidth;
        this.isJumping = false;
      }
      this.goTo(this.index - 1);
    } else if (this.index > 0) {
      this.goTo(this.index - 1);
    }
    this.restartAutoplay();
  }

  update() {
    this.goTo(this.index, false);
    if (this.loopEnabled) this.normalizeIndex();
  }

  onDotClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    const logical = Number.parseInt(button.dataset.index || '0', 10);
    if (!Number.isFinite(logical)) return;

    if (this.loopEnabled) {
      // Prefer the slide in the middle set that matches this logical index
      this.goTo(this.realCount + logical);
    } else {
      this.goTo(logical);
    }
    this.restartAutoplay();
  }

  onPointerDown(event) {
    if (this.realCount <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    // Keep mouse drag smooth (avoid image drag / text select)
    if (event.pointerType !== 'touch') {
      event.preventDefault();
    }

    if (this.loopEnabled) this.normalizeIndex();

    this.isDragging = true;
    this.dragMoved = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragLastX = event.clientX;
    this.dragLastTime = performance.now();
    this.dragVelocity = 0;
    this.dragDeltaX = 0;
    this.stopAutoplay();

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.setPointerCapture?.(event.pointerId);
    target.classList.add('is-dragging');
    if (this.track instanceof HTMLElement) {
      this.track.style.transitionDuration = '0ms';
    }
    this.setTrackOffset(this.baseOffset(), false);
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;
    if (event.pointerType !== 'touch') event.preventDefault();

    const now = performance.now();
    const dt = Math.max(8, now - this.dragLastTime);
    const instantDx = event.clientX - this.dragLastX;
    // EMA velocity for smoother flick detection
    const instantVelocity = instantDx / dt;
    this.dragVelocity = this.dragVelocity * 0.65 + instantVelocity * 0.35;
    this.dragLastX = event.clientX;
    this.dragLastTime = now;
    this.dragDeltaX = event.clientX - this.dragStartX;

    if (Math.abs(this.dragDeltaX) > 4) this.dragMoved = true;

    if (this.dragRaf != null) return;
    this.dragRaf = window.requestAnimationFrame(() => {
      this.dragRaf = null;
      if (!this.isDragging) return;
      this.setTrackOffset(this.baseOffset() - this.dragDeltaX, false);
    });
  }

  onPointerUp(event) {
    if (!this.isDragging || (event.pointerId != null && event.pointerId !== this.pointerId)) return;

    if (this.dragRaf != null) {
      window.cancelAnimationFrame(this.dragRaf);
      this.dragRaf = null;
    }

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.classList.remove('is-dragging');
    try {
      target.releasePointerCapture?.(this.pointerId);
    } catch (_) {
      /* already released */
    }

    const deltaX = this.dragDeltaX;
    const velocity = this.dragVelocity;
    this.isDragging = false;
    this.pointerId = null;
    this.dragDeltaX = 0;
    this.dragVelocity = 0;

    this.settleAfterDrag(deltaX, velocity);
    if (!this.isPointerInside) this.startAutoplay();
  }

  onClickCapture(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  handleVisibility() {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayEnabled || this.reduceMotion?.matches || document.hidden) return;
    if (this.isPointerInside && this.canHoverPause) return;
    this.autoplayTimer = window.setInterval(() => this.next(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer != null) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.startAutoplay();
  }

  cleanup() {
    this.stopAutoplay();
    if (this.dragRaf != null) {
      window.cancelAnimationFrame(this.dragRaf);
      this.dragRaf = null;
    }
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.abortController?.abort();
    this.abortController = null;
  }
}

customElements.get('mn-image-text-slider') || customElements.define('mn-image-text-slider', MnImageTextSlider);
